import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { usePos } from '../../contexts/PosContext';
import { orders as ordersApi, notifications as notificationsApi } from '../../api/client';
import {
  LayoutDashboard, Package, GitBranch, Users, CreditCard,
  FileText, LogOut, ChevronLeft, ChevronRight, Building2,
  UserSquare2, Receipt, ShoppingBag, ShieldCheck, Bell, Settings, KeyRound, ScanLine
} from 'lucide-react';
import { colors } from '../../theme';
import ChangePasswordModal from '../ChangePasswordModal';
import { FISCALIZATION_ENABLED } from '../../config/features';

// `pos: true` marks the few entries an installed till has. It is not a permission — a till is a
// counter, not a back office: everything else is decided in the office and would only slow the
// machine at the till down (see contexts/PosContext.js).
const nav = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, always: true },
  { to: '/products', label: 'Produktet', icon: Package, always: true, pos: true },
  { to: '/orders', label: 'Porositë', icon: ShoppingBag, manage: true, badge: 'orders' },
  { to: '/notifications', label: 'Njoftimet', icon: Bell, always: true, badge: 'notif' },
  { to: '/invoices', label: 'Faturat', icon: Receipt, manage: true },
  // `fiscal: true` is hidden with the module (config/features). A till is the exception below.
  { to: '/fiscalization', label: 'Fiskalizimi', icon: ScanLine, always: true, pos: true, fiscal: true },
  { to: '/clients', label: 'Klientët', icon: UserSquare2, manage: true },
  { to: '/branches', label: 'Degët', icon: GitBranch, manage: true },
  { to: '/workers', label: 'Punëtorët', icon: Users, manage: true },
  { to: '/ec-declarations', label: 'Deklarata EC', icon: FileText, always: true },
  { to: '/billing', label: 'Pagesa', icon: CreditCard, owner: true },
  { to: '/admin', label: 'Admin', icon: ShieldCheck, admin: true },
  // On a till this is where a sync is forced, the catalogue refreshed and the PC unbound.
  { to: '/settings', label: 'Cilësimet', icon: Settings, always: true, pos: true },
];

export default function Sidebar() {
  const { user, company, logout, canManage, isOwner, isAdmin } = useAuth();
  const { isPos, branchName } = usePos();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { data: newCount = 0 } = useQuery({
    queryKey: ['orders-new-count'],
    queryFn: () => ordersApi.newCount().then(r => r.data.count),
    // Orders are approved in the office; a till never shows them, so it never polls for them.
    enabled: canManage && !isPos,
    refetchInterval: 60000,
  });

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: () => notificationsApi.unreadCount().then(r => r.data.count),
    enabled: !isPos,
    refetchInterval: 60000,
  });

  const badgeCountFor = (badge) => (badge === 'orders' ? newCount : badge === 'notif' ? unreadCount : 0);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <aside style={{
      width: collapsed ? 70 : 248,
      height: '100vh',
      background: colors.sidebar,
      color: '#e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s',
      flexShrink: 0
    }}>
      {/* Header */}
      <div style={{ padding: '18px 14px', borderBottom: '1px solid #2a2a3c', display: 'flex', alignItems: 'center', gap: 8, justifyContent: collapsed ? 'center' : 'space-between' }}>
        {!collapsed && (
          <div style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: colors.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Building2 size={17} color="#fff" />
              </div>
              <span style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#f8fafc' }}>{isAdmin ? 'StockFlow' : company?.name}</span>
            </div>
            {/* On a till the branch matters more than the role: it is the only branch this PC sells for. */}
            <div style={{ fontSize: 11, color: '#8b8ba0', marginTop: 4, paddingLeft: 2 }}>
              {isAdmin ? 'Platform Admin' : isPos ? `Arka · ${branchName || 'dega'}` : user?.role}
            </div>
          </div>
        )}
        <button onClick={() => setCollapsed(c => !c)} style={{ background: 'none', border: 'none', color: '#8b8ba0', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex' }}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '10px 10px', overflowY: 'auto' }}>
        {nav.map(item => {
          // An installed till is a counter: the branch's products, fiscalization, its own settings.
          if (isPos && !item.pos) return null;
          // Fiscalization is not offered in the back office while it is off — but a till is a till,
          // and taking it away there would leave the counter with nothing to sell with.
          if (item.fiscal && !FISCALIZATION_ENABLED && !isPos) return null;
          // The platform admin is an operator: only the Admin dashboard and account Settings.
          if (isAdmin && !item.admin && item.to !== '/settings') return null;
          if (item.manage && !canManage) return null;
          if (item.owner && !isOwner) return null;
          if (item.admin && !isAdmin) return null;
          const Icon = item.icon;
          const badgeCount = badgeCountFor(item.badge);
          const showBadge = !!item.badge && badgeCount > 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              style={({ isActive }) => ({
                position: 'relative',
                display: 'flex', alignItems: 'center', gap: 11,
                padding: collapsed ? '11px 0' : '10px 12px',
                marginBottom: 3,
                borderRadius: 11,
                justifyContent: collapsed ? 'center' : 'flex-start',
                textDecoration: 'none',
                color: isActive ? '#fff' : '#a3a3b8',
                background: isActive ? colors.gradient : 'transparent',
                boxShadow: isActive ? '0 6px 16px rgba(99,102,241,0.35)' : 'none',
                transition: 'all 0.15s',
                fontSize: 14, fontWeight: isActive ? 600 : 500,
              })}
            >
              <span style={{ position: 'relative', display: 'flex' }}>
                <Icon size={18} />
                {showBadge && collapsed && (
                  <span style={badgeDot} />
                )}
              </span>
              {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
              {!collapsed && showBadge && (
                <span style={{ background: colors.danger, color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 700, minWidth: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{badgeCount}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Account / profile — change password lives here, not in Settings */}
      <div style={{ borderTop: '1px solid #2a2a3c', padding: collapsed ? '10px 0' : '12px 12px 6px' }}>
        <button
          onClick={() => setShowPassword(true)}
          title="Llogaria — ndrysho fjalëkalimin"
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: collapsed ? '10px 0' : '8px 10px', borderRadius: 11,
            justifyContent: collapsed ? 'center' : 'flex-start',
            background: 'rgba(255,255,255,0.04)', border: '1px solid #2a2a3c',
            color: '#e2e8f0', cursor: 'pointer', textAlign: 'left'
          }}
        >
          <div style={{ width: 30, height: 30, borderRadius: 9, background: colors.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#fff' }}>
            {(user?.firstName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
          </div>
          {!collapsed && (
            <div style={{ overflow: 'hidden', flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Llogaria'}
              </div>
              <div style={{ fontSize: 11, color: '#8b8ba0', display: 'flex', alignItems: 'center', gap: 4 }}>
                <KeyRound size={11} /> Ndrysho fjalëkalimin
              </div>
            </div>
          )}
        </button>
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        style={{
          display: 'flex', alignItems: 'center', gap: 11,
          padding: collapsed ? '14px 0' : '13px 22px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          background: 'none', border: 'none', color: '#f87171',
          cursor: 'pointer', borderTop: '1px solid #2a2a3c',
          fontSize: 14, width: '100%', fontWeight: 500
        }}
      >
        <LogOut size={18} />
        {!collapsed && <span>Dil</span>}
      </button>

      <ChangePasswordModal open={showPassword} onClose={() => setShowPassword(false)} />
    </aside>
  );
}

const badgeDot = {
  position: 'absolute', top: -3, right: -3, width: 9, height: 9,
  borderRadius: '50%', background: colors.danger, border: '2px solid #13131f',
};
